import { useTranslation } from 'react-i18next';
import { XIcon } from '@phosphor-icons/react';
import { Logo } from './ui/Logo';
import { useUserCompaniesStore } from '../stores/userCompanies.store';
import { useSelectedCompanyStore } from '../stores/selectedCompany.store';
import { hasPermission, Permission } from '@monorepo/shared';
import { SidebarNavigation } from './sidebar/SidebarNavigation';
import { SidebarCompanySection } from './sidebar/SidebarCompanySection';
import { SidebarUserMenu } from './sidebar/SidebarUserMenu';
import { useNavigate } from 'react-router-dom';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
  const { t } = useTranslation();
  const companies = useUserCompaniesStore((state) => state.companies);
  const selectedCompanyId = useSelectedCompanyStore((state) => state.selectedCompanyId);
  const setSelectedCompany = useSelectedCompanyStore((state) => state.setSelectedCompany);
  const navigate = useNavigate();

  const selectedCompany = companies.find((c) => c.id === selectedCompanyId);
  const isMainContactForSelectedCompany = selectedCompany?.isMainContact || false;
  const currentUserRole = selectedCompany?.role;
  const canViewUserManagement = currentUserRole
    ? hasPermission(currentUserRole, Permission.VIEW_USER_MANAGEMENT)
    : false;

  const translateClassName = isOpen ? 'translate-x-0' : '-translate-x-full';

  return (
    <aside
      className={`fixed left-0 top-0 z-40 flex h-screen w-80 max-w-[85vw] flex-col overflow-y-auto bg-[#f4f5f6] transition-transform duration-200 lg:translate-x-0 ${translateClassName}`}
    >
      <div className="flex items-center justify-between p-6">
        <button type="button" onClick={() => navigate('/')} className="flex items-center">
          <Logo className="h-10" />
        </button>

        <button
          type="button"
          onClick={onClose}
          aria-label={t('sidebar.close_menu')}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 transition hover:bg-gray-200 lg:hidden"
        >
          <XIcon size={22} />
        </button>
      </div>

      <SidebarNavigation
        selectedCompanyId={selectedCompanyId}
        canViewUserManagement={canViewUserManagement}
      />

      <div className="flex-1" />

      <SidebarCompanySection
        companies={companies}
        selectedCompanyId={selectedCompanyId}
        selectedCompany={selectedCompany}
        isMainContactForSelectedCompany={isMainContactForSelectedCompany}
        setSelectedCompany={setSelectedCompany}
      />

      <SidebarUserMenu />
    </aside>
  );
};
